import * as React from 'react';
import { State } from 'xstate';
import { ADD_MMO, COMMIT, DELETE, REORDER_SCREENS, FlowMachineContext, OVERRIDE_SCREEN_PREVIEW, SET_CURRENT_OBJECT, SET_POSITION, SET_SIZE, SET_INDEX } from '../fsm/types';
import { MachineContext } from '../fsm/useMachine';
import html2canvas from 'html2canvas'
import ImageAsset from '../../model/Assets';

// Global synchronous flag to track interaction state (bypasses async state machine)
export const interactionState = { isInteracting: false };

/**
 * Check if a screen needs its preview captured (missing or invalid URL)
 */
const screenNeedsPreview = (screen: any): boolean => {
    const url = screen?.previewScreenShot?.externalURL;
    if (!url) return true;

    // Handle tagged value format
    const urlValue = typeof url === 'object' && url?.__tag === 'string' ? url.value : url;

    // Need capture if: empty, data URL (base64), File object, or non-http string
    if (!urlValue) return true;
    if (typeof urlValue === 'object') return true; // File object
    if (typeof urlValue === 'string' && !urlValue.startsWith('http')) return true;

    return false;
};

export const updateListener = () => {
    const [current, send, service] = React.useContext(MachineContext)
    const previewTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
    const pendingPreviewRef = React.useRef<{ screenIndex: number } | null>(null);
    const capturedScreensRef = React.useRef<Set<number>>(new Set());
    const isCapturingAllRef = React.useRef(false); // Flag to prevent subscription interference during bulk capture

    // Wait for all images in a container to be loaded
    const waitForImages = (container: HTMLElement): Promise<void> => {
        const images = container.querySelectorAll('img');
        const promises = Array.from(images).map((img) => {
            if (img.complete) return Promise.resolve();
            return new Promise<void>((resolve) => {
                img.onload = () => resolve();
                img.onerror = () => resolve(); // Don't block on failed images
                // Timeout after 3 seconds to prevent hanging
                setTimeout(resolve, 3000);
            });
        });
        return Promise.all(promises).then(() => {});
    };

    const generatePreviewForScreen = (capturedScreenIndex: number) => {
        // Clear any existing timeout
        if (previewTimeoutRef.current) {
            clearTimeout(previewTimeoutRef.current);
        }

        previewTimeoutRef.current = setTimeout(async () => {
            // Wait for next frame to ensure DOM has updated with new positions
            requestAnimationFrame(async () => {
                // Verify we're still on the same screen before generating preview
                const currentState = service.getSnapshot();
                if (currentState?.context.index !== capturedScreenIndex) {
                    // Screen changed while waiting - skip preview generation
                    return;
                }

                // Wait for all images to load before capturing
                const container = document.getElementById("container");
                if (!container) return;
                await waitForImages(container);

                console.log("[update-listener] Generating preview for screen", capturedScreenIndex);
                html2canvas(container, {
                    useCORS: true,
                    allowTaint: true,
                    logging: false,
                    scale: 0.5, // Lower resolution for faster capture
                    ignoreElements: (element) => {
                        return element.classList.contains('moveable-control-box') ||
                               element.classList.contains('moveable-line') ||
                               element.classList.contains('moveable-control');
                    }
                }).then((canvas) => {
                    try {
                        const img_src = canvas.toDataURL("image/png")
                        if (img_src && img_src.startsWith("data:image")) {
                            console.log("[update-listener] Preview generated successfully for screen", capturedScreenIndex);
                            send({ type: OVERRIDE_SCREEN_PREVIEW, image: ImageAsset.default({ __tag: "string", value: img_src }), screenIndex: capturedScreenIndex })
                            capturedScreensRef.current.add(capturedScreenIndex);
                        } else {
                            console.error("[update-listener] Invalid canvas data URL generated");
                        }
                    } catch (error) {
                        // CORS tainted canvas - toDataURL fails
                        console.warn("[update-listener] toDataURL failed (likely CORS), screen", capturedScreenIndex, error);
                        capturedScreensRef.current.add(capturedScreenIndex);
                    }
                }).catch((error) => {
                    console.error("[update-listener] html2canvas failed:", error);
                })
            });
        }, 100); // 100ms debounce - only generate preview if no actions for 100ms
    };

    /**
     * Capture all screens' previews on initial load.
     * This iterates through each screen, switches to it, captures, then moves on.
     */
    const captureAllScreensOnLoad = React.useCallback(async () => {
        const state = service.getSnapshot();
        if (!state) return;

        // Prevent subscription from interfering during bulk capture
        isCapturingAllRef.current = true;

        const screens = state.context.flow.screens;
        const originalIndex = state.context.index;

        console.log("[update-listener] Capturing previews for all", screens.length, "screens on load");

        for (let i = 0; i < screens.length; i++) {
            const screen = screens[i];

            // Check if this screen needs preview
            if (screenNeedsPreview(screen)) {
                // Switch to this screen
                if (service.getSnapshot()?.context.index !== i) {
                    send({ type: SET_INDEX, index: i });
                    // Wait for DOM to render
                    await new Promise(resolve => setTimeout(resolve, 250));
                }

                // Capture using html2canvas
                const container = document.getElementById("container");
                if (container) {
                    try {
                        const canvas = await html2canvas(container, {
                            useCORS: true,
                            allowTaint: true,
                            logging: false,
                            scale: 0.5,
                            ignoreElements: (element) => {
                                return element.classList.contains('moveable-control-box') ||
                                       element.classList.contains('moveable-line') ||
                                       element.classList.contains('moveable-control');
                            }
                        });
                        const img_src = canvas.toDataURL("image/png");
                        if (img_src && img_src.startsWith("data:image")) {
                            send({
                                type: OVERRIDE_SCREEN_PREVIEW,
                                image: ImageAsset.default({ __tag: "string", value: img_src }),
                                screenIndex: i
                            });
                            capturedScreensRef.current.add(i);
                            console.log(`[update-listener] Captured preview for screen ${i}`);
                        }
                    } catch (error) {
                        console.error(`[update-listener] Failed to capture screen ${i}:`, error);
                    }
                }

                // Small delay between captures
                await new Promise(resolve => setTimeout(resolve, 50));
            } else {
                capturedScreensRef.current.add(i);
            }
        }

        // Return to original screen
        if (service.getSnapshot()?.context.index !== originalIndex) {
            send({ type: SET_INDEX, index: originalIndex });
        }

        // Re-enable subscription handling
        isCapturingAllRef.current = false;

        console.log("[update-listener] Finished capturing all screen previews");
    }, [send, service]);

    // Generate previews for all screens in background after editor is ready
    // DISABLED: This was causing blank screen issues by cycling through screens on load
    // Previews will now only be captured when user navigates to each screen
    // const initialPreviewGenerated = React.useRef(false);
    // React.useEffect(() => {
    //     if (!initialPreviewGenerated.current) {
    //         initialPreviewGenerated.current = true;
    //         const scheduleCapture = () => {
    //             if (typeof requestIdleCallback !== 'undefined') {
    //                 requestIdleCallback(() => {
    //                     captureAllScreensOnLoad();
    //                 }, { timeout: 5000 });
    //             } else {
    //                 setTimeout(() => {
    //                     captureAllScreensOnLoad();
    //                 }, 2000);
    //             }
    //         };
    //         setTimeout(scheduleCapture, 100);
    //     }
    // }, [service, captureAllScreensOnLoad]);

    React.useEffect(() => {
        const subscription = service.subscribe(async (state: State<FlowMachineContext, any, {}>) => {
           // Skip preview generation during active user interaction to prevent flicker
           // Check BOTH the synchronous flag and the context state
           if (interactionState.isInteracting || state.context.isInteracting) {
               // Store that we need to generate a preview later
               pendingPreviewRef.current = { screenIndex: state.context.index };
               return;
           }

           // If interaction just ended and we have a pending preview, generate it
           if (!interactionState.isInteracting && !state.context.isInteracting && pendingPreviewRef.current) {
               const screenIndex = pendingPreviewRef.current.screenIndex;
               pendingPreviewRef.current = null;

               // Generate preview for the pending screen
               generatePreviewForScreen(screenIndex);
               return;
           }

           // Generate previews for events that change canvas appearance
           // Also generate when navigating to a screen that doesn't have a preview yet
           // REORDER_SCREENS is triggered when adding/duplicating/deleting screens
           if ([ADD_MMO, COMMIT, DELETE, REORDER_SCREENS].includes(state.event.type)) {
                // Capture screenIndex at event time, not after async operations
                const screenIndex = state.event.screenIndex !== undefined ? state.event.screenIndex : state.context.index;

                // IMPORTANT: Capture current index at time of event to prevent race conditions
                const capturedScreenIndex = screenIndex;

                // Generate preview for this screen
                generatePreviewForScreen(capturedScreenIndex);
           }
           // Also capture preview when navigating to a screen that needs one
           // Skip if we're in the middle of bulk capture on load
           else if (state.event.type === SET_INDEX && !isCapturingAllRef.current) {
                const screenIndex = state.context.index;
                const screen = state.context.flow.screens[screenIndex];

                // Only generate if this screen hasn't been captured and needs preview
                if (!capturedScreensRef.current.has(screenIndex) && screenNeedsPreview(screen)) {
                    // Delay slightly to let the screen render
                    setTimeout(() => {
                        generatePreviewForScreen(screenIndex);
                    }, 200);
                }
           }
        });

        return () => {
            subscription.unsubscribe();
            if (previewTimeoutRef.current) {
                clearTimeout(previewTimeoutRef.current);
            }
        };
    }, [service]); // note: service should never change
}